import { DateRange, HolidayKey, HOLIDAY_KEYS } from "./types";

/*
 * Datumlogik för högtidsteman.
 *
 * Fikahjulet används oftast en gång i veckan, därför visas ett tema hela
 * ISO-veckan (måndag–söndag) som högtiden infaller i, även om högtiden
 * ligger tidigare i veckan. Alla datum räknas fram algoritmiskt så att det
 * fungerar för godtyckliga år utan externa dataset.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export const startOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const addDays = (d: Date, days: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);

/** Måndagen i den ISO-vecka som `d` tillhör. */
export const startOfIsoWeek = (d: Date): Date => {
  const day = (d.getDay() + 6) % 7; // 0 = måndag ... 6 = söndag
  return addDays(startOfDay(d), -day);
};

/** Söndagen i den ISO-vecka som `d` tillhör. */
export const endOfIsoWeek = (d: Date): Date => addDays(startOfIsoWeek(d), 6);

export const isoWeekRange = (d: Date): DateRange => ({
  start: startOfIsoWeek(d),
  end: endOfIsoWeek(d),
});

/** Första veckodagen `weekday` (0 = sön ... 6 = lör) på eller efter `d`. */
const nextWeekdayOnOrAfter = (d: Date, weekday: number): Date => {
  const diff = (weekday - d.getDay() + 7) % 7;
  return addDays(d, diff);
};

/**
 * Påskdagen enligt Meeus/Jones/Butchers algoritm (gregoriansk kalender):
 * första söndagen efter första fullmånen efter vårdagjämningen.
 */
export const easterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = mars, 4 = april
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

/** Midsommarafton: fredagen mellan 19 och 25 juni. */
export const midsummerEve = (year: number): Date =>
  nextWeekdayOnOrAfter(new Date(year, 5, 19), 5);

/** Midsommardagen: lördagen mellan 20 och 26 juni. */
export const midsummerDay = (year: number): Date =>
  addDays(midsummerEve(year), 1);

export const halloween = (year: number): Date => new Date(year, 9, 31);

/** Luciadagen, 13 december. */
export const luciaDay = (year: number): Date => new Date(year, 11, 13);

export const christmasEve = (year: number): Date => new Date(year, 11, 24);

/**
 * Antal extra veckor före högtidsveckan som temat ska visas.
 * Julveckan är till stor del ledighet, därför får julen även veckan innan.
 */
const EXTRA_WEEKS_BEFORE: Partial<Record<HolidayKey, number>> = {
  christmas: 1,
};

/** Det datumintervall (inklusivt) då temat ska visas för ett givet år. */
export const holidayRange = (holiday: HolidayKey, year: number): DateRange => {
  let range: DateRange;
  switch (holiday) {
    case "easter":
      // Veckan som slutar på påskdagen: skärtorsdag, långfredag, påskafton, påskdagen.
      range = isoWeekRange(easterSunday(year));
      break;
    case "midsummer":
      range = isoWeekRange(midsummerEve(year));
      break;
    case "halloween":
      range = isoWeekRange(halloween(year));
      break;
    case "lucia":
      range = isoWeekRange(luciaDay(year));
      break;
    case "christmas":
      range = isoWeekRange(christmasEve(year));
      break;
  }
  const extra = EXTRA_WEEKS_BEFORE[holiday] ?? 0;
  if (extra > 0) {
    range = { ...range, start: addDays(range.start, -7 * extra) };
  }
  if (holiday === "christmas") {
    // Luciaveckan har företräde: julens extravecka börjar först efter den.
    const lucia = isoWeekRange(luciaDay(year));
    if (range.start.getTime() <= lucia.end.getTime()) {
      range = { ...range, start: addDays(lucia.end, 1) };
    }
  }
  return range;
};

export const isWithinRange = (d: Date, range: DateRange): boolean => {
  const t = startOfDay(d).getTime();
  return t >= range.start.getTime() && t <= range.end.getTime();
};

/** Alla temaperioder för ett år, i kalenderordning. */
export const holidayRangesForYear = (
  year: number
): Array<{ holiday: HolidayKey; range: DateRange }> =>
  HOLIDAY_KEYS.map(holiday => ({
    holiday,
    range: holidayRange(holiday, year),
  })).sort((a, b) => a.range.start.getTime() - b.range.start.getTime());

/**
 * Vilken högtid (om någon) som är aktiv ett visst datum.
 * Kontrollerar även angränsande år så att perioder över årsskiftet fungerar.
 */
export const getActiveHoliday = (date: Date = new Date()): HolidayKey | null => {
  const year = date.getFullYear();
  for (const y of [year - 1, year, year + 1]) {
    for (const holiday of HOLIDAY_KEYS) {
      if (isWithinRange(date, holidayRange(holiday, y))) {
        return holiday;
      }
    }
  }
  return null;
};

/** Nästa kommande temaperiod räknat från `date` (inklusive en pågående). */
export const getNextHoliday = (
  date: Date = new Date()
): { holiday: HolidayKey; range: DateRange } | null => {
  const today = startOfDay(date).getTime();
  const year = date.getFullYear();
  const candidates = [
    ...holidayRangesForYear(year),
    ...holidayRangesForYear(year + 1),
  ]
    .filter(c => c.range.end.getTime() >= today)
    .sort((a, b) => a.range.start.getTime() - b.range.start.getTime());
  return candidates[0] ?? null;
};

export const daysBetween = (a: Date, b: Date): number =>
  Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY_MS);
